import type { ConversationId, MessageId } from '@cyphex/types';
import { ROUTES } from '@/constants/routes';
import { MESSAGE_PARAM } from '../constants';

/** In-app path for one message: `/chat/<conversationId>?m=<messageId>`. */
export function messagePath(conversationId: ConversationId, messageId: MessageId): string {
  return `${ROUTES.chatThread(conversationId)}?${MESSAGE_PARAM}=${messageId}`;
}

/** Absolute URL, which is what belongs on the clipboard. */
export function messagePermalink(conversationId: ConversationId, messageId: MessageId): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return `${origin}${messagePath(conversationId, messageId)}`;
}

/**
 * Copy a permalink. Falls back to a hidden textarea because `navigator.clipboard`
 * is unavailable on insecure origins — which is exactly where a self-hosted
 * deployment gets first tried.
 */
export async function copyPermalink(
  conversationId: ConversationId,
  messageId: MessageId,
): Promise<boolean> {
  const url = messagePermalink(conversationId, messageId);
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }

  try {
    const field = document.createElement('textarea');
    field.value = url;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(field);
    return ok;
  } catch {
    return false;
  }
}
